(function () {
  const containerSelector = '[data-klarna-express-container]';
  const payloadSelector = '[data-klarna-express-payload]';
  const errorSelector = '[data-klarna-express-error]';

  const getConfig = () => window.YonKlarnaExpress || {};

  const setError = (container, message) => {
    const wrapper = container.closest('[data-klarna-express-wrap]');
    const errorEl = wrapper && wrapper.querySelector(errorSelector);

    if (!errorEl) return;

    errorEl.textContent = message || '';
    errorEl.toggleAttribute('hidden', !message);
  };

  const getPayload = (container) => {
    const wrapper = container.closest('[data-klarna-express-wrap]');
    const payloadEl = wrapper && wrapper.querySelector(payloadSelector);

    if (!payloadEl) return null;

    try {
      return JSON.parse(payloadEl.textContent);
    } catch (error) {
      console.error('Unable to parse Klarna Express checkout payload', error);
      return null;
    }
  };

  const normalizeAddress = (response) => {
    const address = response && response.collected_shipping_address;

    if (!address) return {};

    return {
      email: address.email,
      firstName: address.given_name,
      lastName: address.family_name,
      address1: address.street_address,
      address2: address.street_address2,
      city: address.city,
      province: address.region,
      zip: address.postal_code,
      country: address.country,
      phone: address.phone,
    };
  };

  const buildCheckoutUrl = (address) => {
    const config = getConfig();
    const checkoutUrl = config.checkoutUrl || '/checkout';
    const params = new URLSearchParams();

    if (address.email) params.set('checkout[email]', address.email);
    if (address.firstName) params.set('checkout[shipping_address][first_name]', address.firstName);
    if (address.lastName) params.set('checkout[shipping_address][last_name]', address.lastName);
    if (address.address1) params.set('checkout[shipping_address][address1]', address.address1);
    if (address.address2) params.set('checkout[shipping_address][address2]', address.address2);
    if (address.city) params.set('checkout[shipping_address][city]', address.city);
    if (address.province) params.set('checkout[shipping_address][province]', address.province);
    if (address.zip) params.set('checkout[shipping_address][zip]', address.zip);
    if (address.country) params.set('checkout[shipping_address][country]', address.country);
    if (address.phone) params.set('checkout[shipping_address][phone]', address.phone);

    const query = params.toString();
    return query ? `${checkoutUrl}?${query}` : checkoutUrl;
  };

  const saveKlarnaResult = async (response) => {
    const attributes = {
      'Klarna authorization token': response.authorization_token || '',
      'Klarna session id': response.session_id || '',
    };

    const address = response.collected_shipping_address;

    if (address) {
      attributes['Klarna collected shipping address'] = JSON.stringify(address);
    }

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });
  };

  const handleAuthorizationResponse = async (container, response) => {
    console.info('Klarna Express checkout authorization result', response);

    if (!response || !response.approved) {
      setError(container, 'Klarna checkout was not approved. Please use the checkout button instead.');
      return;
    }

    try {
      await saveKlarnaResult(response);
    } catch (error) {
      console.error('Unable to save Klarna authorization details to the cart', error);
    }

    window.location.href = buildCheckoutUrl(normalizeAddress(response));
  };

  const handleClick = (container, authorize) => {
    const payload = getPayload(container);

    if (!payload) {
      setError(container, 'Klarna checkout is unavailable right now. Please use the checkout button instead.');
      return;
    }

    setError(container, '');

    try {
      authorize(
        {
          collect_shipping_address: true,
        },
        payload,
        (response) => {
          handleAuthorizationResponse(container, response);
        }
      );
    } catch (error) {
      console.error('Unable to start Klarna Express checkout', error);
      setError(container, 'Klarna checkout is unavailable right now. Please use the checkout button instead.');
    }
  };

  const renderButtons = () => {
    const config = getConfig();

    if (!config.clientId || !window.Klarna || !window.Klarna.Payments) return;

    // Klarna Express only ever renders in ONE place (the cart-page footer; it is
    // disabled in the cart drawer). A cart re-render can briefly leave a second
    // container in the DOM, which is what makes the button appear duplicated.
    // Keep only the last (freshest) container and drop any extras.
    const containers = Array.from(document.querySelectorAll(containerSelector));
    if (containers.length > 1) {
      containers.slice(0, -1).forEach((extra) => {
        const wrap = extra.closest('[data-klarna-express-wrap]');
        (wrap || extra).remove();
      });
    }

    const container = document.querySelector(containerSelector);
    if (!container) return;

    const payload = getPayload(container);

    if (!payload || !payload.order_amount || !payload.order_lines || !payload.order_lines.length) {
      return;
    }

    // Identity guard: once this exact element has been initialised we never load
    // again. A cart re-render swaps in a fresh element (no flag), so it renders
    // once. Checking only the flag — not child count — avoids a double-load
    // during Klarna's async button injection, which is what stacked the button.
    if (container.dataset.klarnaExpressLoaded === 'true') {
      return;
    }

    container.style.width = '100%';
    container.style.maxWidth = '100%';
    container.style.minWidth = '0';
    // Guarantee a single button even if a previous render left one behind.
    container.replaceChildren();
    container.dataset.klarnaExpressLoaded = 'true';

    window.Klarna.Payments.Buttons.init({
      client_id: config.clientId,
    }).load(
      {
        container,
        theme: container.dataset.klarnaTheme || 'default',
        shape: container.dataset.klarnaShape || 'default',
        locale: config.locale,
        on_click: (authorize) => handleClick(container, authorize),
      },
      (result) => {
        if (result && result.show_form === false) {
          setError(container, 'Klarna checkout is unavailable for this cart.');
        }
      }
    );
  };

  let renderTimer = null;

  const scheduleRender = () => {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(renderButtons, 100);
  };

  window.YonKlarnaExpressLoad = renderButtons;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRender);
  } else {
    scheduleRender();
  }

  ['shapes:cart:update', 'shapes:cart:afteradditem', 'shapes:cart:cartqtychange', 'theme:cart-drawer:open'].forEach((eventName) => {
    window.addEventListener(eventName, scheduleRender);
    document.addEventListener(eventName, scheduleRender);
  });

  // Re-render when the cart DOM changes, but ignore mutations that originate
  // inside a Klarna container (the button injecting its own iframe) so Klarna's
  // own DOM activity does not retrigger renders in a loop.
  new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      const target = mutation.target;
      return !(
        target instanceof Element &&
        typeof target.closest === 'function' &&
        target.closest(containerSelector)
      );
    });

    if (relevant) scheduleRender();
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
